export type Credentials = {
    username: string;
    password: string;
};

// https://msdn.microsoft.com/en-us/library/windows/desktop/ms725481(v=vs.85).aspx
export type SystemTime = {
    year: number;
    month: number;
    dayOfWeek: number;
    weekOfMonth: number; // Not strictly part of SYSTEMTIME... but the Day field is repurposed this way for timezone start/stop data
    hour: number;
    minute: number;
    // seconds and milliseconds are ignored for timezone start/stop data
};

export type TimezoneDetails = {
    bias: number; // minutes to subtract from UTC
    standardBias: number; // minutes to add to bias in standard time
    daylightBias: number; // minutes to add to bias in daylight savings time
    standardStart: SystemTime | null;
    daylightStart: SystemTime | null;
};

export type ServerDetails = {
    appVersion: string;
    devicePackVersion: string;
    copyrightYear: number;
    timezone: TimezoneDetails;
    reportsPath: string;
    reportsProtocol: 'http' | 'https';
    reportsPort: number;
    reportTypes: ReportType[];
    refreshRate: number;
    availableLanguages: Language[];
    features: {[key: string]: boolean};
};

export type SessionId = string;

export type DeviceGroupId = string;

export type DeviceGroup = {
    groupId: DeviceGroupId;
    name: string;
    parentGroupId: DeviceGroupId;
    isDevicesGroup: boolean;
    isThinClientsGroup: boolean;
};

export type DeviceGroupMap = {
    [groupId: string]: DeviceGroup;
};

export type ServerId = number;

export type DeviceIndex = number;

export type DeviceId = string;

export type DeviceType =
    'PTZ'
    | 'Fixed'
    | 'AccessDevice'
    | 'Integration'
     // TODO: add other types
;

export type Device = {
    deviceId: DeviceId;
    deviceType: DeviceType;
    deviceIndex: DeviceIndex;
    serverId: ServerId;
    name: string;
    groupIds: DeviceGroupId[];
};

export type DeviceMap = {
    [deviceId: string]: Device;
};

export type DeviceIdMap = {
    [indexString: string]: DeviceId;
};

// Note: StreamId is internal to the Web Client
export type StreamId = string; // `${deviceId}_${streamIndex}`

export type StreamIndex = 0 | 1 | 2 | 3;

export type Stream = {
    deviceId: DeviceId;
    streamIndex: StreamIndex;
    width: number;
    height: number;
};

export type Camera = Device & {
    streams: Stream[];
    ptzLocations: string[];
    canControlZoom: boolean;
    canControlPtz: boolean;
    canPtzPresets: boolean;
    canViewLive: boolean;
    canViewHistorical: boolean;
    canSupportAudioIn: boolean;
    canSupportAudioOut: boolean;
    hasAiObjectTracking: boolean;
};

export type CameraMessage = {
    s: number; // state
    e: number; // event
    t: number; // time
    a: string; // alarmId
    p: string; // policyId
    fake?: boolean; // Only appears as a placeholder in a fake message generated during timeline construction
};

export type MinutesPerSegment = 5 | 10 | 15 | 30 | 60 | 120 | 240 | 360;

export const zoomLevels = Object.freeze<MinutesPerSegment>([ 5, 10, 15, 30, 60, 120, 240, 360 ]);

export const getNextZoomLevel = (minutesPerSegment: MinutesPerSegment, zoomIn: boolean): MinutesPerSegment => {
    // Find the index of the current zoom level
    const i = zoomLevels.indexOf(minutesPerSegment);
    if (i < 0) {
        return 30; // Should never happen. Go with the default
    }
    const iNext = i + (zoomIn ? -1 : 1);
    return zoomLevels[Math.max(0, Math.min(iNext, zoomLevels.length - 1))];
};

export type MarkerType = 'Alarm' | 'AccessDenied' | 'AccessGranted' | 'AccessOther';
 // Note: order dictates z-index. Last item appears on top
export const markerTypes = Object.freeze<MarkerType>([ 'AccessGranted', 'AccessOther', 'AccessDenied', 'Alarm' ]);

export type Marker = {
    type: MarkerType;
    position: number;
    data: CameraMessage;
};
export type CompoundMarker = {
    type: MarkerType;
    position: number;
    markers: Marker[];
};
export type CompoundMarkersMap = {
    [markerType: string]: CompoundMarker[][];
};

export type TimelineBar = {
    x: number;
    y: number;
    width: number;
    height: number;
    type: string; // a partial CSS class name used to determine the styling of the bar
};
export type Timeline = {
    baseTime: number; // UTC milliseconds since 1970-01-01
    displayWidth: number; // width in pixels of the entire display (i.e. including labels and margins)
    displayHeight: number; // height in pixels of the entire display (i.e. including labels and margins)
    segmentWidth: number; // width in pixels of each segment of the timeline
    segmentHeight: number; // height in pixels of each segment (just the bar height, does not include marker overhang)
    segmentMargin: number; // distance in pixels between each segment (just between the bars; ignores markers)
    labelWidth: number; // How many pixels at the left of the timeline to reserve for time labels
    minutesPerSegment: MinutesPerSegment; // how many minutes appear in each segment
    scale: number; // multiplier from milliseconds to pixels
    segments: number;
    bars: TimelineBar[];
    markers: CompoundMarkersMap; // Compound markers per segment
};

export const MaxCells = 17;
export type LayoutSize = 1 | 4 | 9 | 16;

export const isValidLayoutSize = (size: number) => size === 1 || size === 4 || size === 9 || size === 16;


// Cell 0 is a special-case cell used on the Change Image screen
// Cells 1 through 16 show up on the Monitoring page
export type CellIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16;

export type DurableCellState = {
    streamId: StreamId | null;
    playbackSpeed: number; // TODO: need to define this better
    minutesPerSegment: MinutesPerSegment;
    sync: boolean; // true to synchronize zooming and scrolling across other sync'ed cells; false to change zoom and scroll independently
    scroll: number; // [ 0 .. 1 ] indicates how much of the height is scrolled (0 = at the top, 1 = at the bottom)
    live: boolean; // true if this cell is showing live video; or false for historical video
    playing: boolean; // true if the cell is playing video; false if showing a still frame
    mute: boolean; // true if the cell is not playing audio; false if playing audio
    streamPosition: UtcTime; // approximate time of where we are in the video (with latency from true time because we can't update this at FPS of the video)
    // If you change this type, you MUST also update durableCellStateProperties (see below).
};

// Unfortunately, TypeScript doesn't expose a way to generate an array of keys into JavaScript.
// So we need to manually keep track of the property names that are in DurableCellState.
// All properties listed here will be persisted in localStorage for each cell.
export const durableCellStateProperties: (keyof DurableCellState)[] = [
    'streamId',
    'playbackSpeed',
    'minutesPerSegment',
    'sync',
    'scroll',
    'live',
    'playing',
    'streamPosition',
];

export type TransientCellState = {
    connecting: boolean;
    connected: boolean;
    seeking: boolean;
    frame: string | null;
    error: Error | null;
    timeline: Timeline | null;
    waitingForDisconnectTime: boolean; // true if we need to stop video playback and capture the time at which video stopped playing
};

export type CellState = DurableCellState & TransientCellState;

export type UtcTime = number;  // number of milliseconds since Jan 1, 1970 12:00:00am UTC
export type UtcTicks = number; // number of ticks (100-nanoseconds) since Jan 1, 1970 12:00:00am UTC -- used for video frame times

export type SeekType =
    'to'
    | 'prev-frame'
    | 'prev-1sec'
    | 'prev-10sec'
    | 'prev-alarm'
    | 'prev-activity'
    | 'next-frame'
    | 'next-1sec'
    | 'next-10sec'
    | 'next-alarm'
    | 'next-activity'
;

// tslint:disable-next-line:no-any
export type Policy = any; // TODO: what are we doing this with?
export type PolicyId = string; // TODO: what are we doing with this? load these ahead of time or just render to string on server and leave it alone on client?

export type PolicyMap = {
    [policyId: string]: Policy;
};

export type AlarmId = string;

export type Alarm = {
    alarmId: AlarmId;
    deviceId: DeviceId;
    xml: XML;
    policyId: PolicyId;
    time: UtcTime;
};

export type AlarmMap = {
    [alarmId: string]: Alarm;
};

export type InstantMessageId = string;

export type InstantMessage = {
    id: InstantMessageId;
    text: string;
    senderName: string;
    utcTime: number;
};

export type InstantMessageMap = {
    [id: string]: InstantMessage;
};

export type PollData = {
    alarms?: AlarmMap;
    cameraMessages?: {
        [deviceId: string]: ReadonlyArray<CameraMessage>;
    };
    messages?: InstantMessageMap;
    refreshDevices?: boolean;
};

export enum CameraCommand {
    MOVE_UP = 0,
    MOVE_DOWN = 1,
    MOVE_RIGHT = 2,
    MOVE_LEFT = 3,
    ZOOM_IN = 4,
    ZOOM_OUT = 5,
    RETURN_HOME = 6,
    NAVIGATION = 7,
    IRIS1 = 8,
    IRIS2 = 9,
    FOCUS1 = 10,
    FOCUS2 = 11,
    BRIGHT1 = 12,
    BRIGHT2 = 13,
    CONTRAST1 = 14,
    CONTRAST2 = 15,
    PRESET1 = 16,
    PRESET2 = 17,
    PRESET3 = 18,
    PRESET4 = 19,
    PRESET5 = 20,
    PRESET6 = 21,
    PRESET7 = 22,
    PRESET8 = 23,
    UNLOCK = 24,
    LOCK = 25,
    //CONFIG_TOUR = 26,
}

export type ReportId = number;

export type ReportType =
    'Alarms'
    | 'AlarmStats'
    | 'HeatMap'
    | 'AcrossLine'
    | 'ObjectCounts'
    | 'ObjectStats'
    | 'LPR'
    | 'InStock'
    | 'FaceRec'
    | 'UserActivity'
    | 'OverheadPeopleCount'
;

export type ReportOutput = 'Graph' | 'Table';

export type ScheduleType = 'Hourly' | 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
export const ScheduleTypes = Object.freeze<ScheduleType>([ 'Hourly', 'Daily', 'Weekly', 'Monthly', 'Yearly' ]);

export type XML = string;

export type Size = {
    readonly width: number;
    readonly height: number;
};

export type GraphType = 'Bar' | 'Line';

export type HeatMapType = 'Paths' | 'DwellTime';

export type Report = {
    reportId: ReportId;
    name: string;
    owner: string;
    reportType: ReportType;
    lastRun?: UtcTime;
    created: UtcTime;
    shared: boolean;
    scheduled: boolean;
    lastScheduleRun?: UtcTime;
    deleted?: boolean; // for optimistic deletions
    settings: XML;
};

export type ReportMap = {
    [reportId: string]: Report;
};

export type LprPlateList = {
    id: number;
    name: string;
};

export type ReportGraphSize = {
    id: 'small' | 'medium' | 'large';
    width: number;
    height: number;
};

export const reportGraphSizes: ReportGraphSize[] = [
    { id: 'small',  width:  600, height: 400 },
    { id: 'medium', width:  800, height: 600 },
    { id: 'large',  width: 1000, height: 800 },
];

export type Permissions = {
    viewAlarms: boolean;
    executeReport: boolean;
};

export type Language = {
    code: string;
    name: string;
};



// WEBPACK FOOTER //
// ./src/types.ts